import axios from "axios";
import CryptoJS from "crypto-js";

// Configuración base de la API
const API_URL = process.env.REACT_APP_API_URL;
const ENCRYPTION_SECRET = process.env.REACT_APP_ENCRYPTION_SECRET;

const paymentService = {
  // ==================== FUNCIONES DE ENCRIPTACIÓN/DESENCRIPTACIÓN ====================

  decryptResponse: (encryptedData, iv) => {
    try {
      if (!encryptedData) {
        console.error("No hay datos para desencriptar");
        return null;
      }

      const key = CryptoJS.SHA256(ENCRYPTION_SECRET).toString(CryptoJS.enc.Hex);
      const ivWordArray = CryptoJS.enc.Base64.parse(iv);

      const options = {
        iv: ivWordArray,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      };

      const decrypted = CryptoJS.AES.decrypt(
        encryptedData,
        CryptoJS.enc.Hex.parse(key),
        options,
      );
      const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedStr) {
        return null;
      }

      return decryptedStr;
    } catch (error) {
      console.error("Error en decryptResponse:", error);
      return null;
    }
  },

  extractUrlFromText: (text) => {
    if (!text) return null;

    const patterns = [
      /https?:\/\/checkout\.akua\.la\/links\/[a-zA-Z0-9-]+(?:\?sandbox=true)?/g,
      /https?:\/\/[^\s"']+\.akua\.la[^\s"']*/g,
      /"url":\s*"([^"]+)"/g,
      /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/g,
    ];

    for (const pattern of patterns) {
      const matches = text.match(pattern);
      if (matches && matches.length > 0) {
        let url = matches[0];
        const urlMatch = url.match(/"url":\s*"([^"]+)"/);
        if (urlMatch) {
          url = urlMatch[1];
        }
        if (url && url.startsWith("http")) {
          return url;
        }
      }
    }
    return null;
  },

  extractIdFromText: (text) => {
    if (!text) return null;

    const patterns = [
      /"id":\s*"([a-zA-Z0-9-]+)"/g,
      /id:\s*"([a-zA-Z0-9-]+)"/g,
      /lnk-[a-zA-Z0-9-]+/g,
    ];

    for (const pattern of patterns) {
      const matches = text.match(pattern);
      if (matches && matches.length > 0) {
        let id = matches[0];
        const idMatch = id.match(/"id":\s*"([^"]+)"/);
        if (idMatch) {
          id = idMatch[1];
        }
        return id;
      }
    }
    return null;
  },

  extractDataFromText: (text) => {
    if (!text) return null;

    try {
      const jsonData = JSON.parse(text);
      return jsonData;
    } catch (e) {}

    const result = {};
    const url = paymentService.extractUrlFromText(text);
    if (url) result.url = url;
    const id = paymentService.extractIdFromText(text);
    if (id) result.id = id;
    return Object.keys(result).length > 0 ? result : null;
  },

  // ==================== FUNCIÓN PARA BUSCAR FACTURAS POR NIT Y CÓDIGO ====================

  searchInvoicesByNitAndCode: async (nit, code) => {
    try {
      if (!nit || nit.trim().length < 3) {
        throw new Error("El NIT debe tener al menos 3 caracteres");
      }

      if (!code || code.trim().length < 3) {
        throw new Error(
          "El código empresarial debe tener al menos 3 caracteres",
        );
      }

      const payload = {
        nit: nit.trim(),
        code: code.trim(),
      };

      const response = await axios.post(
        `${API_URL}/payment/get-invoices`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      const responseData = response.data;
      let invoices = [];

      // Verificar si la respuesta viene encriptada
      if (responseData.data && responseData.data.iv && responseData.data.data) {
        const decryptedText = paymentService.decryptResponse(
          responseData.data.data,
          responseData.data.iv,
        );

        if (decryptedText) {
          try {
            const decryptedData = JSON.parse(decryptedText);

            if (Array.isArray(decryptedData)) {
              invoices = decryptedData;
            } else if (
              decryptedData.invoices &&
              Array.isArray(decryptedData.invoices)
            ) {
              invoices = decryptedData.invoices;
            } else if (
              decryptedData.data &&
              Array.isArray(decryptedData.data)
            ) {
              invoices = decryptedData.data;
            } else if (
              typeof decryptedData === "object" &&
              decryptedData !== null
            ) {
              invoices = [decryptedData];
            }
          } catch (e) {
            console.error("Error parseando JSON desencriptado:", e);
            const extractedData =
              paymentService.extractDataFromText(decryptedText);
            if (extractedData) {
              invoices = [extractedData];
            }
          }
        }
      } else if (Array.isArray(responseData.data)) {
        invoices = responseData.data;
      } else if (
        responseData.data &&
        Array.isArray(responseData.data.invoices)
      ) {
        invoices = responseData.data.invoices;
      } else if (
        responseData.invoices &&
        Array.isArray(responseData.invoices)
      ) {
        invoices = responseData.invoices;
      }

      // Mapear los datos directamente según la estructura recibida
      const formattedInvoices = invoices.map((invoice) => ({
        id: invoice.id || `inv_${Date.now()}_${Math.random()}`,
        fecha:
          invoice.createdAt ||
          invoice.fecha ||
          new Date().toLocaleString("es-CO"),
        nombreEmpresa:
          invoice.company ||
          invoice.nombreEmpresa ||
          invoice.empresa ||
          "Empresa",
        nitEmpresa: invoice.nit || invoice.nitEmpresa || nit,
        numeroFactura:
          invoice.invoice_number ||
          invoice.numeroFactura ||
          invoice.facturaNumero ||
          "N/A",
        valorPagar: parseFloat(
          invoice.amount || invoice.valorPagar || invoice.monto || 0,
        ),
        estado:
          invoice.status_invoice ||
          invoice.status ||
          invoice.estado ||
          "pendiente",
        status: invoice.status || invoice.status_invoice || "pendiente",
        amount: invoice.amount,
        code: invoice.code,
        user_id: invoice.user_id,
        payment: invoice.payment,
        more: invoice.more,
        deletedAt: invoice.deletedAt,
        updatedAt: invoice.updatedAt,
        createdAt: invoice.createdAt,
        paymentUrl: null,
        paymentId: null,
        paymentUrlUsed: false,
        paymentWindowOpened: false,
      }));

      return {
        code: responseData.code || 0,
        data: formattedInvoices,
        message: responseData.message || "successful",
      };
    } catch (error) {
      console.error("❌ Error al buscar facturas:", error);

      let errorMessage = "Error al conectar con el servidor";
      if (error.response?.status === 404) {
        errorMessage = "Servicio de búsqueda no disponible";
      } else if (error.response?.status === 500) {
        errorMessage = "Error interno del servidor";
      } else if (error.code === "ECONNABORTED") {
        errorMessage = "Tiempo de espera agotado";
      } else if (error.message === "Network Error") {
        errorMessage =
          "No se puede conectar al servidor. Verifica que el backend esté corriendo";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      throw new Error(errorMessage);
    }
  },

  // ==================== FUNCIONES PRINCIPALES DE PAGOS ====================

  createPayment: async (amount, companyData = {}) => {
    try {
      const payload = {
        amount: amount,
        name: companyData.companyName || "Cliente GoPass",
        nit: companyData.nit || "000000000-0",
        bill_number:
          companyData.facturaNumero || companyData.bill_number || "000000",
      };

      const response = await axios.post(`${API_URL}/payment/payment`, payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const responseData = response.data;
      let paymentUrl = null;
      let paymentId = null;

      if (responseData.data && responseData.data.iv && responseData.data.data) {
        const decryptedText = paymentService.decryptResponse(
          responseData.data.data,
          responseData.data.iv,
        );

        if (decryptedText) {
          const extractedData =
            paymentService.extractDataFromText(decryptedText);
          if (extractedData) {
            paymentUrl = extractedData.url;
            paymentId = extractedData.id;
          }
        }
      } else if (responseData.url) {
        paymentUrl = responseData.url;
        paymentId = responseData.id;
      } else if (responseData.data && responseData.data.url) {
        paymentUrl = responseData.data.url;
        paymentId = responseData.data.id;
      }

      if (!paymentUrl) {
        throw new Error("No se pudo obtener la URL de pago");
      }

      if (paymentId) {
        paymentService.storePaymentId(paymentId);
      }

      return {
        code: responseData.code || 0,
        data: {
          url: paymentUrl,
          id: paymentId || `payment_${Date.now()}`,
        },
        message: responseData.message || "successful",
      };
    } catch (error) {
      let errorMessage = "Error al conectar con el servidor de pagos";
      if (error.response?.status === 404) {
        errorMessage = "Servicio de pagos no disponible";
      } else if (error.response?.status === 500) {
        errorMessage = "Error interno del servidor de pagos";
      } else if (error.code === "ECONNABORTED") {
        errorMessage = "Tiempo de espera agotado";
      } else if (error.message === "Network Error") {
        errorMessage =
          "No se puede conectar al servidor. Verifica que el backend esté corriendo";
      }

      throw new Error(errorMessage);
    }
  },

  // ==================== FUNCIONES DE ACTUALIZACIÓN DE ESTADO ====================

  changePaymentStatus: async (id, status) => {
    try {
      if (!id) {
        throw new Error("ID de transacción no proporcionado");
      }

      if (!status || (status !== "APPROVED" && status !== "CANCELED")) {
        throw new Error("Estado inválido. Debe ser 'APPROVED' o 'CANCELED'");
      }

      const payload = {
        id: parseInt(id) || id,
        status: status,
      };

      const response = await axios.post(
        `${API_URL}/payment/change-status`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      if (response.data && response.data.code === 0) {
        paymentService.updatePaymentStatus(id, status.toLowerCase());
      }

      return {
        success: true,
        data: response.data,
        message: `Estado actualizado a ${status} correctamente`,
      };
    } catch (error) {
      console.error("❌ Error al cambiar estado del pago:", error);

      let errorMessage = "Error al actualizar el estado del pago";
      if (error.response?.status === 404) {
        errorMessage = "Transacción no encontrada";
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || "Datos inválidos";
      } else if (error.response?.status === 500) {
        errorMessage = "Error interno del servidor";
      }

      return {
        success: false,
        error: errorMessage,
        originalError: error,
      };
    }
  },

  acceptPayment: async (id) => {
    return paymentService.changePaymentStatus(id, "APPROVED");
  },

  cancelPayment: async (id) => {
    return paymentService.changePaymentStatus(id, "CANCELED");
  },

  // ==================== OBTENER TRANSACCIÓN POR ID ====================
  getTransactionById: async (id) => {
    try {
      if (!id) {
        console.warn("getTransactionById: ID no proporcionado");
        return { code: -1, data: null, message: "ID no proporcionado" };
      }

      const response = await axios.get(`${API_URL}/payment/payment-id/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const responseData = response.data;

      if (responseData.data && responseData.data.iv && responseData.data.data) {
        const decryptedText = paymentService.decryptResponse(
          responseData.data.data,
          responseData.data.iv,
        );

        if (decryptedText) {
          try {
            const decryptedData = JSON.parse(decryptedText);

            if (decryptedData.link || decryptedData.payment) {
              return {
                code: responseData.code || 0,
                data: decryptedData,
                message: responseData.message || "successful",
              };
            }

            return {
              code: responseData.code || 0,
              data: decryptedData,
              message: responseData.message || "successful",
            };
          } catch (e) {
            console.error("Error parseando JSON desencriptado:", e);
          }
        }
      }

      return responseData;
    } catch (error) {
      console.error("Error obteniendo transacción:", error);

      const localData = paymentService.getStoredPaymentData(id);
      if (localData) {
        return {
          code: 0,
          data: localData,
          message: "successful (from cache)",
        };
      }

      return { code: -1, data: null, message: "Error al obtener transacción" };
    }
  },

  // ==================== FUNCIONES DE ALMACENAMIENTO LOCAL ====================

  storePaymentUrlSecurely: (paymentId, url) => {
    if (paymentId) {
      sessionStorage.setItem(`payment_url_${paymentId}`, url);
    }
  },

  getSecurePaymentUrl: (paymentId) => {
    return sessionStorage.getItem(`payment_url_${paymentId}`);
  },

  storePaymentId: (paymentId) => {
    if (paymentId) {
      sessionStorage.setItem("current_payment_id", paymentId);
      localStorage.setItem(
        `payment_data_${paymentId}`,
        JSON.stringify({
          id: paymentId,
          fecha: new Date().toISOString(),
          status: "pending",
        }),
      );
    }
  },

  getCurrentPaymentId: () => {
    return sessionStorage.getItem("current_payment_id");
  },

  clearCurrentPaymentId: () => {
    sessionStorage.removeItem("current_payment_id");
  },

  storePaymentData: (paymentId, data) => {
    if (paymentId && data) {
      localStorage.setItem(`payment_data_${paymentId}`, JSON.stringify(data));
    }
  },

  getStoredPaymentData: (paymentId) => {
    if (!paymentId) return null;
    const stored = localStorage.getItem(`payment_data_${paymentId}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error("Error parseando datos almacenados:", e);
        return null;
      }
    }
    return null;
  },

  updatePaymentStatus: (paymentId, status, additionalData = {}) => {
    if (!paymentId) return;

    const existingData = paymentService.getStoredPaymentData(paymentId);
    const updatedData = {
      ...existingData,
      ...additionalData,
      id: paymentId,
      status: status,
      fechaActualizacion: new Date().toISOString(),
    };

    localStorage.setItem(
      `payment_data_${paymentId}`,
      JSON.stringify(updatedData),
    );
  },

  cleanExpiredPaymentUrls: () => {
    const keys = Object.keys(sessionStorage);
    keys.forEach((key) => {
      if (key.startsWith("payment_url_")) {
        sessionStorage.removeItem(key);
      }
    });
  },

  cleanOldPaymentData: () => {
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const keys = Object.keys(localStorage);

    keys.forEach((key) => {
      if (key.startsWith("payment_data_")) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (
            data &&
            data.fecha &&
            new Date(data.fecha).getTime() < thirtyDaysAgo
          ) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          localStorage.removeItem(key);
        }
      }
    });
  },

  // ==================== FUNCIONES DE VALIDACIÓN ====================

  validatePaymentUrl: (url) => {
    try {
      const validDomains = ["checkout.akua.la", "akua.la", "api.akua.la"];
      const urlObj = new URL(url);
      return validDomains.some(
        (domain) =>
          urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`),
      );
    } catch {
      return false;
    }
  },

  isValidPaymentId: (id) => {
    if (!id) return false;
    const patterns = [
      /^\d+$/,
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      /^[a-zA-Z0-9-]+$/,
      /^lnk-[a-zA-Z0-9-]+$/,
    ];
    return patterns.some((pattern) => pattern.test(id));
  },
};

export default paymentService;
